<script setup lang="ts">
const route = useRoute();
const breadcrumbState = useGlobalBreadcrumbs();

function ensureTrailingSlash(path: string) {
  return path.endsWith("/") ? path : `${path}/`;
}

function segmentToLabel(segment: string) {
  const decoded = decodeURIComponent(segment);

  if (/^\d+(st|nd|rd|th)$/i.test(decoded)) {
    return decoded.toUpperCase();
  }

  return decoded
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function buildFallbackBreadcrumbs(path: string) {
  const cleanPath = path.split("?")[0] || "/";

  if (cleanPath === "/") {
    return [{ label: "Home", to: "/" }];
  }

  const segments = cleanPath.split("/").filter(Boolean);
  const items = [{ label: "Home", to: "/" }];
  let currentPath = "";

  segments.forEach((segment) => {
    currentPath += `/${segment}`;
    items.push({
      label: segmentToLabel(segment),
      to: ensureTrailingSlash(currentPath),
    });
  });

  return items;
}

const breadcrumbItems = computed(() => {
  const state = breadcrumbState.value;

  if (state.path === route.path && state.items.length) {
    return state.items;
  }

  return buildFallbackBreadcrumbs(route.path);
});

const shouldHide = computed(() => {
  if (route.meta?.hideGlobalBreadcrumbs) {
    return true;
  }

  const state = breadcrumbState.value;

  if (state.path === route.path) {
    return state.hidden || breadcrumbItems.value.length <= 1;
  }

  return breadcrumbItems.value.length <= 1;
});
</script>

<template>
  <nav
    v-if="!shouldHide"
    aria-label="Breadcrumb"
    class="border-b border-slate-200 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-950/80"
  >
    <div class="mx-auto max-w-6xl px-4 py-3 sm:px-6 lg:px-8">
      <ol class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600 dark:text-slate-300">
        <li
          v-for="(item, index) in breadcrumbItems"
          :key="`${item.to}-${item.label}`"
          class="inline-flex items-center gap-2"
        >
          <span v-if="index > 0" class="text-slate-400 dark:text-slate-500">/</span>
          <NuxtLink
            v-if="index < breadcrumbItems.length - 1"
            :to="item.to"
            class="transition hover:text-sky-700 dark:hover:text-sky-300"
          >
            {{ item.label }}
          </NuxtLink>
          <span v-else class="font-medium text-slate-900 dark:text-white">{{ item.label }}</span>
        </li>
      </ol>
    </div>
  </nav>
</template>
